import type {
  ZipkinTraceSpan,
  TimeRange,
  TimelineTick,
  SpanData,
} from './types';
import { getRunStatusClassName } from '@cotera/client/v0/lib/utils';

export const getTimeRange = <T = any>(
  spans: ZipkinTraceSpan<T>[]
): TimeRange => {
  if (spans.length === 0) {
    return {
      start: Date.now(),
      end: Date.now() + 1000,
      duration: 1000,
      unit: 'ms',
      step: 100,
      format: (time: number) => `${time}ms`,
    };
  }

  // Collect all start and end times from flat array
  const allTimes = spans.flatMap((span) => [
    span.startTime,
    span.startTime + span.duration,
  ]);

  const minTime = Math.min(...allTimes);
  const maxTime = Math.max(...allTimes);
  const totalDuration = maxTime - minTime;

  // Add asymmetric padding - less at start (5%), more at end (25%) to accommodate labels
  const startPadding = totalDuration * 0.05;
  const endPadding = totalDuration * 0.25;
  const start = minTime - startPadding;
  const end = maxTime + endPadding;
  const duration = end - start;

  // Determine appropriate time unit and step
  // Aim for approximately 8-12 ticks maximum to avoid collisions
  const targetTickCount = 10;

  if (duration < 1000) {
    // Less than 1 second - use milliseconds
    return {
      start,
      end,
      duration,
      unit: 'ms',
      step: Math.max(50, Math.ceil(duration / targetTickCount)),
      format: (time: number) => `${Math.round(time)}ms`,
    };
  } else if (duration < 60000) {
    // Less than 1 minute - use seconds
    return {
      start,
      end,
      duration,
      unit: 's',
      step: Math.max(0.1, duration / targetTickCount / 1000),
      format: (time: number) => `${(time / 1000).toFixed(1)}s`,
    };
  } else {
    // 1 minute or more - use minutes
    return {
      start,
      end,
      duration,
      unit: 'm',
      step: Math.max(0.1, duration / targetTickCount / 60000),
      format: (time: number) => `${(time / 60000).toFixed(1)}m`,
    };
  }
};

// Re-export the unified status utilities
export {
  getRunStatusClassName as getStatusClassName,
  getStatusIndicatorClassName,
} from '../../lib/utils';

export const formatDuration = (duration: number, _unit: string): string => {
  // Duration is in milliseconds
  if (duration < 1000) {
    // Less than 1 second - show milliseconds
    return `${Math.round(duration)}ms`;
  } else if (duration < 60000) {
    // Less than 1 minute - show seconds
    return `${Math.round(duration / 1000)}s`;
  } else {
    // 1 minute or more - show minutes and seconds
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.round((duration % 60000) / 1000);
    if (seconds === 0) {
      return `${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  }
};

export const calculateTimelineTicks = (
  timeRange: TimeRange
): TimelineTick[] => {
  const ticks = [];

  // Use different intervals based on total duration:
  // < 10s: 1 second intervals
  // < 60s: 5 second intervals
  // >= 60s: 1 minute intervals
  const oneMinuteMs = 60000;
  const tenSecondsMs = 10000;
  const fiveSecondsMs = 5000;
  const oneSecondMs = 1000;

  let tickInterval: number;
  if (timeRange.duration < tenSecondsMs) {
    tickInterval = oneSecondMs;
  } else if (timeRange.duration < oneMinuteMs) {
    tickInterval = fiveSecondsMs;
  } else {
    tickInterval = oneMinuteMs;
  }

  // Generate ticks at appropriate intervals
  for (
    let time = timeRange.start;
    time <= timeRange.end;
    time += tickInterval
  ) {
    const elapsedMs = time - timeRange.start;

    // Skip the 0 label
    if (elapsedMs < tickInterval / 2) {
      continue;
    }

    let label: string;
    if (tickInterval === oneMinuteMs) {
      const minutes = Math.round(elapsedMs / oneMinuteMs);
      label = `${minutes}m`;
    } else {
      const seconds = Math.round(elapsedMs / 1000);
      label = `${seconds}s`;
    }

    ticks.push({
      time,
      position: ((time - timeRange.start) / timeRange.duration) * 100,
      label,
    });
  }

  return ticks;
};

export const calculateSpanData = <T = any>(
  spans: ZipkinTraceSpan<T>[],
  timeRange: TimeRange
): SpanData<T>[] => {
  // Work with flat array directly - depth comes from span.data
  return spans.map((span) => {
    const left =
      ((span.startTime - timeRange.start) / timeRange.duration) * 100;
    const width = (span.duration / timeRange.duration) * 100;

    // Extract depth from span data (backend provides this)
    const depth = (span.data as any).depth ?? 0;

    return {
      ...span,
      left,
      width,
      depth,
      className: span.className || getRunStatusClassName(span.status),
      children: [], // No children in flat structure
      events: span.events, // Pass through events
    };
  });
};

/**
 * Get Tailwind color classes for event type
 */
export const getEventColor = (
  eventType:
    | 'start'
    | 'success'
    | 'fail'
    | 'error'
    | 'retry'
    | 'cancel'
    | 'queued'
    | 'allocated'
): { bg: string; ring: string } => {
  switch (eventType) {
    case 'start':
      return { bg: 'bg-blue-500', ring: 'ring-blue-500/50' };
    case 'success':
      return { bg: 'bg-emerald-500', ring: 'ring-emerald-500/50' };
    case 'fail':
      return { bg: 'bg-red-500', ring: 'ring-red-500/50' };
    case 'error':
      return { bg: 'bg-orange-500', ring: 'ring-orange-500/50' };
    case 'retry':
      return { bg: 'bg-yellow-500', ring: 'ring-yellow-500/50' };
    case 'cancel':
      return { bg: 'bg-gray-400', ring: 'ring-gray-400/50' };
    case 'queued':
      return { bg: 'bg-violet-500', ring: 'ring-violet-500/50' };
    case 'allocated':
      return { bg: 'bg-indigo-500', ring: 'ring-indigo-500/50' };
    default:
      return { bg: 'bg-gray-600', ring: 'ring-gray-600/50' };
  }
};

/**
 * Format event type for display (capitalize)
 */
export const formatEventType = (eventType: string): string => {
  return eventType.charAt(0).toUpperCase() + eventType.slice(1);
};

/**
 * Calculate event position percentage within a span
 */
export const calculateEventPosition = (
  eventTime: number,
  spanStartTime: number,
  spanDuration: number
): number => {
  if (spanDuration === 0) {
    return 0;
  }
  const offset = eventTime - spanStartTime;
  return Math.max(0, Math.min(100, (offset / spanDuration) * 100));
};
